import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {useParams} from 'react-router';
import AntiCutPlayer from 'components/channels/AntiCutPlayer';
import DPlayer from 'components/channels/DPlayer';
import UltraPlayer from 'components/channels/UltraPlayer';
import AppLoader from 'components/common/AppLoader';
import {useAuthContext} from 'contexts/authContext';
import {usePlayerHealth} from 'hooks/usePlayerHealth';
import {getM3UList} from 'utils/getM3UList';
import {playerHealth} from 'utils/playerHealth';
import {
  isDPlayerVariant,
  isUltraPlayerVariant,
  normalizePlayerVariant,
  PROFILE_REFRESH_TIMEOUT_MS,
  resolvePlayerVariantBeforePlayback
} from 'utils/playerVariant';
import {
  browserLocalStorage,
  readAdvancedStatsPreference,
  writeAdvancedStatsPreference
} from 'utils/ultraPlayerStats';

function normalizeSearch(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

export function channelMatchesQuery(channel, query) {
  const normalizedQuery = normalizeSearch(query.trim());
  if (!normalizedQuery) return true;
  return [channel.name, channel.tvgName, channel.groupTitle].some((value) =>
    normalizeSearch(value).includes(normalizedQuery)
  );
}

function channelKey(channel) {
  return `${channel.name || ''}-${channel.url || ''}`;
}

function ChannelsView() {
  const {groupName} = useParams();
  const {getPublicPathFromLocalStorage, socket, playerVariant, refreshProfile} = useAuthContext();
  const [channels, setChannels] = useState([]);
  const [channelQuery, setChannelQuery] = useState('');
  const [catalogStatus, setCatalogStatus] = useState('loading');
  const [catalogError, setCatalogError] = useState(null);
  const [catalogReload, setCatalogReload] = useState(0);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [selectedPlayerVariant, setSelectedPlayerVariant] = useState(null);
  const [startingChannelKey, setStartingChannelKey] = useState(null);
  const [userPaused, setUserPaused] = useState(false);
  const [advancedStatsEnabled, setAdvancedStatsEnabled] = useState(() =>
    readAdvancedStatsPreference(browserLocalStorage())
  );
  const selectionGenerationRef = useRef(0);
  const observedProfileVariantRef = useRef(normalizePlayerVariant(playerVariant));
  const lastPlayedChannelKeyRef = useRef(null);
  const restoreCatalogFocusRef = useRef(false);
  const channelButtonRefs = useRef(new Map());

  const playURL = selectedChannel?.url || '';
  const playingName = selectedChannel?.name || '';
  const activeVariant = normalizePlayerVariant(selectedPlayerVariant || playerVariant || 'C');
  const useUltraPlayer = isUltraPlayerVariant(activeVariant);
  const useDPlayer = isDPlayerVariant(activeVariant);
  const hasAdvancedStats = useUltraPlayer || useDPlayer;
  const {bindPlayer, bindVideoElement, viewId} = usePlayerHealth(playURL, playingName);

  const changeAdvancedStats = useCallback((enabled) => {
    const next = Boolean(enabled);
    setAdvancedStatsEnabled(next);
    writeAdvancedStatsPreference(browserLocalStorage(), next);
  }, []);

  // Remove the preference used by versions that exposed the obsolete classic
  // player. There is now one official resilient experience per server variant.
  useEffect(() => {
    try {
      window.localStorage.removeItem('playerEngine');
    } catch (_) {
      // Private browsing may deny storage access; it does not affect playback.
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    selectionGenerationRef.current += 1;
    setSelectedChannel(null);
    setSelectedPlayerVariant(null);
    setStartingChannelKey(null);
    setUserPaused(false);
    setChannels([]);
    setChannelQuery('');
    setCatalogStatus('loading');
    setCatalogError(null);

    const loadCatalog = async () => {
      try {
        const m3uList = await getM3UList(getPublicPathFromLocalStorage());
        if (cancelled) return;
        const groupChannels = Array.isArray(m3uList?.[groupName]) ? m3uList[groupName] : [];
        setChannels(groupChannels);
        setCatalogStatus(groupChannels.length > 0 ? 'ready' : 'empty');
      } catch (_) {
        if (cancelled) return;
        setChannels([]);
        setCatalogStatus('error');
        setCatalogError(
          'No hemos podido cargar los canales. Comprueba la conexión e inténtalo de nuevo.'
        );
      }
    };
    loadCatalog();
    return () => {
      cancelled = true;
    };
  }, [catalogReload, getPublicPathFromLocalStorage, groupName]);

  const filteredChannels = useMemo(
    () => channels.filter((channel) => channelMatchesQuery(channel, channelQuery)),
    [channelQuery, channels]
  );

  const startChannel = useCallback(
    async (channel) => {
      const generation = selectionGenerationRef.current + 1;
      selectionGenerationRef.current = generation;
      const key = channelKey(channel);
      setStartingChannelKey(key);
      setUserPaused(false);
      observedProfileVariantRef.current = normalizePlayerVariant(playerVariant);
      try {
        // A slow/failed profile refresh keeps the cached assignment: a D user
        // must not silently mount the C engine against D sessions.
        const variant = await resolvePlayerVariantBeforePlayback(
          refreshProfile,
          PROFILE_REFRESH_TIMEOUT_MS,
          normalizePlayerVariant(playerVariant || 'C')
        );
        if (generation !== selectionGenerationRef.current) return;
        setSelectedPlayerVariant(variant);
        setSelectedChannel(channel);
      } finally {
        if (generation === selectionGenerationRef.current) setStartingChannelKey(null);
      }
    },
    [playerVariant, refreshProfile]
  );

  const closePlayer = useCallback(() => {
    selectionGenerationRef.current += 1;
    restoreCatalogFocusRef.current = true;
    setSelectedChannel(null);
    setSelectedPlayerVariant(null);
    setStartingChannelKey(null);
    setUserPaused(false);
  }, []);

  useEffect(() => {
    if (selectedChannel || !restoreCatalogFocusRef.current || catalogStatus !== 'ready') return;
    const button = channelButtonRefs.current.get(lastPlayedChannelKeyRef.current);
    if (!button) return;
    button.scrollIntoView?.({block: 'nearest', inline: 'nearest'});
    button.focus({preventScroll: true});
    restoreCatalogFocusRef.current = false;
  }, [catalogStatus, filteredChannels, selectedChannel]);

  const refreshActiveVariant = useCallback(
    async (reason) => {
      const generation = selectionGenerationRef.current;
      playerHealth.report('player.variantRefresh', {reason, currentVariant: activeVariant});
      const profile = await refreshProfile();
      if (generation !== selectionGenerationRef.current || !profile?.playerVariant)
        return activeVariant;
      const freshVariant = normalizePlayerVariant(profile.playerVariant);
      observedProfileVariantRef.current = freshVariant;
      setSelectedPlayerVariant((current) => {
        const previous = normalizePlayerVariant(current || activeVariant);
        if (previous !== freshVariant) {
          playerHealth.report('player.variantChanged', {
            from: previous,
            to: freshVariant,
            reason
          });
        }
        return freshVariant;
      });
      return freshVariant;
    },
    [activeVariant, refreshProfile]
  );

  // AuthContext refreshes `/me` periodically and when the tab becomes active.
  // Only a genuinely new observed value changes an active transport, so a
  // fallback-to-C decision is not overwritten by a stale cached assignment.
  useEffect(() => {
    const observed = normalizePlayerVariant(playerVariant);
    const previousObserved = observedProfileVariantRef.current;
    observedProfileVariantRef.current = observed;
    if (!selectedChannel || observed === previousObserved) return;
    setSelectedPlayerVariant((current) => {
      const previous = normalizePlayerVariant(current || activeVariant);
      if (previous !== observed) {
        playerHealth.report('player.variantChanged', {
          from: previous,
          to: observed,
          reason: 'profileRefresh'
        });
      }
      return observed;
    });
  }, [activeVariant, playerVariant, selectedChannel]);

  useEffect(() => {
    if (!selectedChannel) return;
    playerHealth.report('player.variantResolved', {variant: activeVariant});
  }, [activeVariant, selectedChannel]);

  // reloadPlayers is a profile-invalidation signal, not a transport restart.
  // Keeping it at this orchestration layer means an unchanged assignment is a
  // no-op, while an admin A/B/C switch swaps engines immediately and safely.
  useEffect(() => {
    if (!selectedChannel || !socket) return undefined;
    const handleReloadPlayers = () => {
      refreshActiveVariant('reloadPlayers');
    };
    socket.on('reloadPlayers', handleReloadPlayers);
    return () => socket.off('reloadPlayers', handleReloadPlayers);
  }, [refreshActiveVariant, selectedChannel, socket]);

  return (
    <>
      {selectedChannel && (
        <Paper
          component="section"
          aria-labelledby="active-channel-title"
          data-testid="playback-surface"
          elevation={0}
          sx={{
            mb: 3,
            overflow: 'clip',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: {xs: 1.5, sm: 2.5}
          }}
        >
          <Box
            data-testid="playback-header"
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 4,
              display: 'grid',
              gridTemplateAreas: {
                xs: '"back title" "meta meta"',
                md: '"back title meta"'
              },
              gridTemplateColumns: {
                xs: 'auto minmax(0, 1fr)',
                md: 'auto minmax(0, 1fr) auto'
              },
              alignItems: 'center',
              gap: {xs: 1, sm: 1.5},
              px: {xs: 1, sm: 1.5},
              py: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper'
            }}
          >
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<ArrowBackIcon />}
              onClick={closePlayer}
              aria-label="Volver a canales"
              sx={{
                gridArea: 'back',
                minWidth: 0,
                borderColor: 'divider',
                color: 'text.primary',
                whiteSpace: 'nowrap',
                '&:hover': {borderColor: 'text.secondary', bgcolor: 'action.hover'}
              }}
            >
              Canales
            </Button>

            <Tooltip title={playingName} placement="bottom-start" enterTouchDelay={0}>
              <Typography
                id="active-channel-title"
                variant="h6"
                noWrap
                sx={{gridArea: 'title', fontWeight: 750, minWidth: 0}}
              >
                {playingName}
              </Typography>
            </Tooltip>

            <Box
              sx={{
                gridArea: 'meta',
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: {xs: 'space-between', md: 'flex-end'},
                gap: 1,
                flexWrap: 'wrap'
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                noWrap
                title={selectedChannel.groupTitle || groupName}
                sx={{minWidth: 0, maxWidth: {xs: '42vw', sm: 240}}}
              >
                {selectedChannel.groupTitle || groupName}
              </Typography>
              <Chip
                label={useUltraPlayer ? 'Ultra C' : `Player ${activeVariant}`}
                color={useUltraPlayer ? 'primary' : useDPlayer ? 'info' : 'secondary'}
                size="small"
                sx={{fontWeight: 700}}
              />
              {hasAdvancedStats && (
                <Tooltip
                  arrow
                  enterTouchDelay={0}
                  title="Muestra la reserva del relay, el buffer local y detalles técnicos sin afectar a la reproducción."
                >
                  <FormControlLabel
                    sx={{m: 0, ml: {xs: 0, sm: 0.5}}}
                    control={
                      <Switch
                        size="small"
                        checked={advancedStatsEnabled}
                        onChange={(event) => changeAdvancedStats(event.target.checked)}
                        slotProps={{
                          input: {
                            'aria-label': `Mostrar datos avanzados de ${useDPlayer ? 'Player D' : 'Ultra C'}`,
                            'aria-expanded': advancedStatsEnabled,
                            'aria-controls': useDPlayer ? 'd-advanced-panel' : 'ultra-advanced-panel'
                          }
                        }}
                      />
                    }
                    label={
                      <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5}}>
                        <QueryStatsIcon aria-hidden="true" fontSize="small" />
                        <Typography variant="body2" sx={{fontWeight: 650}}>
                          Datos avanzados
                        </Typography>
                      </Box>
                    }
                  />
                </Tooltip>
              )}
            </Box>
          </Box>
          <Box sx={{p: {xs: 1, sm: 1.5}}}>
            {useDPlayer ? (
              <DPlayer
                key="player-D"
                playURL={playURL}
                playingName={playingName}
                viewId={viewId}
                bindVideoElement={bindVideoElement}
                userPaused={userPaused}
                onUserPausedChange={setUserPaused}
                onRefreshVariant={refreshActiveVariant}
                advancedStatsEnabled={advancedStatsEnabled}
                socket={socket}
              />
            ) : useUltraPlayer ? (
              <UltraPlayer
                key="player-C"
                playURL={playURL}
                playingName={playingName}
                viewId={viewId}
                bindPlayer={bindPlayer}
                bindVideoElement={bindVideoElement}
                userPaused={userPaused}
                onUserPausedChange={setUserPaused}
                onRefreshVariant={refreshActiveVariant}
                advancedStatsEnabled={advancedStatsEnabled}
              />
            ) : (
              <AntiCutPlayer
                key={`player-${activeVariant}`}
                playURL={playURL}
                playingName={playingName}
                variant={activeVariant}
                viewId={viewId}
                socket={socket}
                bindPlayer={bindPlayer}
                bindVideoElement={bindVideoElement}
                userPaused={userPaused}
                onUserPausedChange={setUserPaused}
                onRefreshVariant={refreshActiveVariant}
              />
            )}
          </Box>
        </Paper>
      )}

      {!selectedChannel && catalogStatus === 'loading' && <AppLoader />}

      {!selectedChannel && catalogStatus === 'error' && (
        <Box role="alert" sx={{py: 6, px: 2, textAlign: 'center'}}>
          <Typography color="error.main" sx={{mb: 2}}>
            {catalogError}
          </Typography>
          <Button variant="contained" onClick={() => setCatalogReload((value) => value + 1)}>
            Volver a cargar
          </Button>
        </Box>
      )}

      {!selectedChannel && catalogStatus === 'empty' && (
        <Box sx={{py: 6, textAlign: 'center'}}>
          <Typography color="text.secondary">No hay canales disponibles en este grupo.</Typography>
        </Box>
      )}

      {!selectedChannel && catalogStatus === 'ready' && (
        <>
          <Box sx={{mb: 2}}>
            <TextField
              fullWidth
              size="small"
              label="Buscar canal"
              value={channelQuery}
              onChange={(event) => setChannelQuery(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />
          </Box>
          {filteredChannels.length === 0 ? (
            <Box sx={{py: 6, textAlign: 'center'}}>
              <Typography color="text.secondary">
                No hay canales que coincidan con la búsqueda.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                gap: 2
              }}
            >
              {filteredChannels.map((channel) => {
                const key = channelKey(channel);
                const isPreparing = startingChannelKey === key;
                return (
                  <Card
                    key={key}
                    aria-busy={isPreparing}
                    sx={{
                      height: 230,
                      display: 'flex',
                      flexDirection: 'column',
                      border: isPreparing ? '2px solid' : '1px solid transparent',
                      borderColor: isPreparing ? 'primary.main' : 'transparent'
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        height: 120,
                        flexShrink: 0,
                        backgroundColor: 'rgba(128,128,128,0.06)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 1.5,
                        boxSizing: 'border-box'
                      }}
                    >
                      <img
                        alt=""
                        src={channel.tvgLogo}
                        onError={(event) => {
                          event.currentTarget.onerror = null;
                          event.currentTarget.src = `${process.env.PUBLIC_URL}/img/channelDefaultImage.png`;
                        }}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                          display: 'block'
                        }}
                      />
                    </Box>
                    <CardContent
                      sx={{
                        flexGrow: 1,
                        pb: '12px !important',
                        pt: 1.5,
                        px: 1.5,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        overflow: 'hidden'
                      }}
                    >
                      <Typography
                        variant="body2"
                        component="h2"
                        sx={{
                          fontWeight: 600,
                          mb: 1.5,
                          textAlign: 'center',
                          lineHeight: 1.3,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {channel.name}
                      </Typography>
                      <Button
                        ref={(node) => {
                          if (node) channelButtonRefs.current.set(key, node);
                          else channelButtonRefs.current.delete(key);
                        }}
                        variant="contained"
                        fullWidth
                        size="small"
                        startIcon={
                          isPreparing ? (
                            <CircularProgress size={15} color="inherit" />
                          ) : (
                            <PlayArrowIcon />
                          )
                        }
                        disabled={isPreparing}
                        onClick={() => {
                          lastPlayedChannelKeyRef.current = key;
                          startChannel(channel);
                        }}
                        aria-label={
                          isPreparing ? `Preparando ${channel.name}` : `Reproducir ${channel.name}`
                        }
                      >
                        {isPreparing ? `Preparando ${channel.name}…` : 'Reproducir'}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          )}
        </>
      )}
    </>
  );
}

export default ChannelsView;
